import { useQuery } from '@tanstack/react-query';
import { Drawer, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { reportsApi, type StoreScopeParams } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type {
  ProductAnalyticsParams,
  ProductAnalyticsRow,
  ProductShipmentLine,
} from '../../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/PageStates';
import { ShipmentStatusTag } from '../../components/ShipmentStatusTag';
import { formatKopeks } from '../../lib/money';
import { groupLinesByDate } from '../../lib/productAnalytics';
import { formatQuantityMilli } from '../../lib/quantity';
import type { ReturnToState } from '../../lib/returnTo';

const ROW_BORDER = '1px solid rgba(0,0,0,0.06)';
const NUMS = { whiteSpace: 'nowrap' as const, fontVariantNumeric: 'tabular-nums' as const };

/**
 * «Где был этот товар»: every накладная line of one product in the period,
 * grouped by business date, each opening its накладная (with a way back to
 * this page), plus the split by store. Always mounted; `product === null`
 * closes it.
 */
export function ProductDrawer({
  product,
  params,
  scope,
  returnTo,
  onClose,
}: {
  product: ProductAnalyticsRow | null;
  params: ProductAnalyticsParams;
  scope: StoreScopeParams;
  returnTo: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const key = product?.key ?? '';
  const query = useQuery({
    queryKey: queryKeys.productShipments(key, params, scope),
    queryFn: () => reportsApi.productShipments(key, params, scope),
    enabled: product !== null,
  });
  const lines = query.data?.lines ?? [];
  const byStore = query.data?.byStore ?? [];
  const groups = groupLinesByDate(lines);
  const period = `${dayjs(params.from).format('D MMM')} – ${dayjs(params.to).format('D MMM YYYY')}`;

  const openDoc = (line: ProductShipmentLine): void => {
    const state: ReturnToState = { returnTo };
    navigate(`/shipments/${line.shipmentId}`, { state });
  };

  return (
    <Drawer
      open={product !== null}
      onClose={onClose}
      title={product ? `${product.name} · ${period}` : ''}
      placement="bottom"
      height="80%"
      styles={{ body: { display: 'grid', gap: 12, alignContent: 'start' } }}
    >
      {product === null ? null : query.isLoading ? (
        <LoadingBlock />
      ) : query.isError || !query.data ? (
        <ErrorBlock onRetry={() => void query.refetch()} />
      ) : lines.length === 0 ? (
        <EmptyBlock description={t('products.empty')} />
      ) : (
        <>
          {byStore.length > 1 ? (
            <div style={{ display: 'grid', gap: 4 }}>
              <Typography.Text strong>{t('products.byStore')}</Typography.Text>
              {byStore.map((s) => (
                <div
                  key={s.storeId}
                  style={{ display: 'flex', gap: 8, justifyContent: 'space-between', fontSize: 13 }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {s.storeName}
                    <Typography.Text type="secondary">
                      {' '}
                      × {formatQuantityMilli(s.quantityMilli)}
                    </Typography.Text>
                  </span>
                  <span style={NUMS}>{formatKopeks(s.issueSumKopeks)}</span>
                  <Typography.Text
                    type={s.profitKopeks < 0 ? 'danger' : 'success'}
                    style={{ ...NUMS, minWidth: 90, textAlign: 'right' }}
                  >
                    {formatKopeks(s.profitKopeks)}
                  </Typography.Text>
                </div>
              ))}
            </div>
          ) : null}

          {groups.map((group) => (
            <div key={group.date}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '6px 4px',
                  background: 'rgba(0,0,0,0.03)',
                  borderRadius: 6,
                }}
              >
                <Typography.Text strong>{dayjs(group.date).format('D MMMM YYYY')}</Typography.Text>
                <Typography.Text strong style={NUMS}>
                  × {formatQuantityMilli(group.quantityMilli)} ·{' '}
                  {formatKopeks(group.issueSumKopeks)}
                </Typography.Text>
              </div>
              {group.lines.map((line) => (
                <div
                  key={line.itemId}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDoc(line)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') openDoc(line);
                  }}
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '10px 4px',
                    borderTop: ROW_BORDER,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 4 }}>
                    <span
                      style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}
                    >
                      <Typography.Link>
                        {t('products.docLink', { name: line.msName })}
                      </Typography.Link>
                      <Typography.Text>{line.storeName}</Typography.Text>
                      {line.status !== 'POSTED' ? <ShipmentStatusTag status={line.status} /> : null}
                      {line.origin === 'MOVED_IN' ? (
                        <Tag color="purple" style={{ margin: 0 }}>
                          {t('products.movedIn')}
                        </Tag>
                      ) : null}
                      {line.msDeletedAt ? (
                        <Tag color="red" style={{ margin: 0 }}>
                          {t('products.deletedInMs')}
                        </Tag>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 13, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span style={NUMS}>
                        × {formatQuantityMilli(line.quantityMilli)}
                        {line.quantityMilli !== line.originalQuantityMilli ? (
                          <Typography.Text type="secondary">
                            {' '}
                            {t('products.originalQty', {
                              qty: formatQuantityMilli(line.originalQuantityMilli),
                            })}
                          </Typography.Text>
                        ) : null}
                      </span>
                      <span style={NUMS}>
                        {line.issuePriceKopeks != null
                          ? t('products.atIssuePrice', {
                              price: formatKopeks(line.issuePriceKopeks),
                            })
                          : t('products.noIssuePrice')}
                      </span>
                      {line.buyPriceKopeks != null ? (
                        <Typography.Text type="secondary" style={NUMS}>
                          {t('products.atBuyPrice', { price: formatKopeks(line.buyPriceKopeks) })}
                        </Typography.Text>
                      ) : null}
                    </span>
                  </div>
                  <Typography.Text strong style={{ marginInlineStart: 8, ...NUMS }}>
                    {line.issueSumKopeks != null ? (
                      formatKopeks(line.issueSumKopeks)
                    ) : (
                      <Typography.Text type="warning">{t('products.noIssuePrice')}</Typography.Text>
                    )}
                  </Typography.Text>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}
